import { useState } from "react";
import type { ServerFingerprint } from "./types";
import {
  Server,
  ExternalLink,
  Search,
  Globe,
  Check,
  Copy,
  Terminal,
  Layers,
  Network
} from "lucide-react";

interface ServerTechTabProps {
  fingerprints: ServerFingerprint[];
  ports: any[];
  technologies: any[];
}

export default function ServerTechTab({ fingerprints, ports, technologies }: ServerTechTabProps) {
  const [headerSearch, setHeaderSearch] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  if (!fingerprints || fingerprints.length === 0) {
    return (
      <div className="py-8 border border-dashed border-gray-800 rounded-lg text-center text-gray-200 text-xs italic">
        No fingerprint details discovered.
      </div>
    );
  }

  const fingerprint = fingerprints[0];
  const allHeaders = fingerprint.all_headers || {};

  // Filter headers based on search query
  const filteredHeaders = Object.entries(allHeaders).filter(([key, val]) =>
    key.toLowerCase().includes(headerSearch.toLowerCase()) ||
    val.toLowerCase().includes(headerSearch.toLowerCase())
  );

  const getHttpStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    if (status >= 300 && status < 400) return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    return "text-red-400 bg-red-500/10 border-red-500/20";
  };

  const handleCopyLine = (key: string, val: string) => {
    navigator.clipboard.writeText(`${key}: ${val}`);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCopyAll = () => {
    const rawText = Object.entries(allHeaders)
      .map(([key, val]) => `${key}: ${val}`)
      .join("\n");
    navigator.clipboard.writeText(rawText);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <div className="space-y-3.5">
      {/* 1. Compact Info Bar */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-2.5 flex flex-wrap items-center justify-between gap-3 shadow-sm text-xs">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Target */}
          <div className="flex items-center gap-1.5">
            <Globe size={13} className="text-orange-500" />
            <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Target:</span>
            <a
              href={fingerprint.target_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[11px] font-bold text-gray-200 hover:text-orange-400 transition-colors flex items-center gap-1"
            >
              {fingerprint.target_url}
              <ExternalLink size={10} />
            </a>
          </div>

          <span className="text-gray-800">|</span>

          {/* Server */}
          <div className="flex items-center gap-1.5">
            <Server size={13} className="text-cyan-400" />
            <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Server:</span>
            <span className="font-mono text-[11px] font-bold text-gray-200">
              {fingerprint.server_info?.server || "Unknown"}
            </span>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Status:</span>
          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${getHttpStatusColor(fingerprint.http_status)}`}>
            {fingerprint.http_status}
          </span>
        </div>
      </div>

      {/* 2. Detected Tech badges */}
      <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-3 space-y-2 shadow-sm">
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-200 uppercase tracking-wider">
          <Layers size={12} className="text-orange-500" />
          <span>Technologies ({technologies.length})</span>
        </div>

        {technologies && technologies.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {technologies.map((tech: any) => (
              <div
                key={tech.name}
                className="bg-gray-800 border border-gray-800 text-gray-200 rounded px-2 py-0.5 flex items-center gap-1.5 text-[11px] font-mono"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                <span className="font-medium text-gray-200">{tech}</span>

              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-gray-200 italic">No web technologies detected.</p>
        )}
      </div>

      {/* 3. Details Grid: Response Headers & Open Ports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Response Headers */}
        <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-3 space-y-2 shadow-sm flex flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-gray-600 pb-2">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-200 uppercase tracking-wider">
              <Terminal size={12} className="text-orange-500" />
              <span>Response Headers</span>
            </div>

            {Object.keys(allHeaders).length > 0 && (
              <div className="flex items-center gap-2">
                {/* Header Search Filter */}
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-200 w-2.5 h-2.5" />
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={headerSearch}
                    onChange={(e) => setHeaderSearch(e.target.value)}
                    className="w-32 sm:w-44 bg-gray-800 border border-gray-800 rounded px-2 py-0.5 pl-6 text-[10px] text-gray-200 placeholder-gray-500 focus:border-orange-500 outline-none transition-all font-mono"
                  />
                </div>

                {/* Copy All Button */}
                <button
                  onClick={handleCopyAll}
                  className="flex items-center gap-1 bg-gray-800 hover:bg-gray-850 border border-gray-800 hover:border-gray-750 text-gray-300 rounded px-2 py-0.5 text-[10px] transition-all select-none font-mono"
                >
                  {copiedAll ? (
                    <>
                      <Check size={10} className="text-emerald-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={10} />
                      Copy All
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Styled Terminal Console Wrapper */}
          <div className="relative border border-gray-800 rounded-lg overflow-hidden bg-gray-800 flex-1 min-h-[200px]">
            {/* Terminal Body */}
            <div className="p-2.5 max-h-52 overflow-y-auto leading-relaxed custom-scrollbar font-mono text-[10px]">
              {Object.keys(allHeaders).length > 0 ? (
                filteredHeaders.length > 0 ? (
                  <div className="space-y-0.5">
                    {filteredHeaders.map(([key, val]) => (
                      <div
                        key={key}
                        className="group flex items-start py-0.5 hover:bg-gray-900/60 rounded px-1.5 transition-colors relative"
                      >
                        <span className="text-cyan-400 font-bold font-mono select-none mr-2 shrink-0">
                          {key}:
                        </span>
                        <span className="text-gray-300 font-mono break-all pr-10">
                          {val}
                        </span>

                        {/* Floating Copy Button per Line */}
                        <button
                          onClick={() => handleCopyLine(key, val)}
                          className="opacity-0 group-hover:opacity-100 absolute right-1.5 top-1/2 -translate-y-1/2 bg-gray-800 border border-gray-800 hover:border-gray-750 text-gray-200 hover:text-white p-0.5 rounded transition-opacity"
                          title="Copy Line"
                        >
                          {copiedKey === key ? (
                            <Check size={8} className="text-emerald-400" />
                          ) : (
                            <Copy size={8} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center text-gray-600 italic">
                    No headers matched filtering query.
                  </div>
                )
              ) : (
                <div className="py-6 text-center text-gray-600 italic">
                  No response headers found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Open Ports */}
        <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-3 space-y-2 shadow-sm flex flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-gray-600 pb-2">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-200 uppercase tracking-wider">
              <Network size={12} className="text-orange-500" />
              <span>Open Ports</span>
            </div>
            {ports && ports.length > 0 && (
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
                {ports.length} Open
              </span>
            )}
          </div>

          <div className="relative border border-gray-800 rounded-lg overflow-hidden bg-gray-800 flex-1 min-h-[200px]">
            <div className="p-2.5 max-h-52 overflow-y-auto leading-relaxed custom-scrollbar font-mono text-[10px]">
              {ports && ports.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-mono">
                    <thead>
                      <tr className="border-b border-gray-600 text-[10px] font-bold text-gray-200 uppercase tracking-wider">
                        <th className="py-1.5 px-2">Port</th>
                        <th className="py-1.5 px-2">Service</th>
                        <th className="py-1.5 px-2 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {ports.map((p, idx) => (
                        <tr key={idx} className="hover:bg-gray-900/60 transition-colors">
                          <td className="py-1.5 px-2 text-cyan-400 font-bold font-mono">
                            {p.port}
                          </td>
                          <td className="py-1.5 px-2 text-gray-300">
                            {p.service || "-"}
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse"></span>
                              <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/20 px-1 py-0.2 rounded">
                                Open
                              </span>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-600 italic">
                  No open ports discovered.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
